/**
 * Spec Phase 6 Slice 6E — paragraph AI suggest popover.
 *
 * Per Phase 6 brief §6: hover-rail AI button opens a tiny inline
 * popover with a textarea + Submit. Submit calls expand on the
 * paragraph with the drafter's intent. v1 uses
 * spec_paragraph_expand for both empty and non-empty paragraphs;
 * the prompt's behavioural rules already handle either case.
 *
 * Renders inline as a positioned overlay above the paragraph, so
 * the cursor stays in the paragraph editor while the drafter
 * types intent.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';

interface Props {
  paragraphNumber: string;
  isPending: boolean;
  onSubmit: (drafterIntent: string) => void;
  onCancel: () => void;
}

export default function ParagraphAiPopover({
  paragraphNumber,
  isPending,
  onSubmit,
  onCancel,
}: Props): JSX.Element {
  const [intent, setIntent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (!isPending) onSubmit(intent.trim());
    }
  };

  return (
    <div
      className="absolute right-8 top-0 z-30 w-72 rounded-sm border border-zinc-300 bg-white p-3 shadow-lg"
      data-testid={`spec-paragraph-ai-popover-${paragraphNumber}`}
      role="dialog"
      aria-label={`AI suggest for paragraph ${paragraphNumber}`}
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-medium text-zinc-600">
          AI suggest — paragraph [{paragraphNumber}]
        </span>
        <button
          type="button"
          onClick={onCancel}
          className="text-zinc-400 hover:text-zinc-700"
          disabled={isPending}
          aria-label="Cancel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <textarea
        ref={textareaRef}
        rows={3}
        className="w-full rounded-sm border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-700 placeholder:text-zinc-400 outline-none focus:border-zinc-500 disabled:opacity-50"
        placeholder="Describe what this paragraph should cover…"
        value={intent}
        onChange={(e) => setIntent(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={isPending}
        data-testid={`spec-paragraph-ai-textarea-${paragraphNumber}`}
      />
      <div className="mt-2 flex items-center justify-end gap-2">
        <span className="mr-auto text-[10px] text-zinc-400">
          ⌘↵ to submit · Esc to cancel
        </span>
        <button
          type="button"
          onClick={onCancel}
          disabled={isPending}
          className="rounded-sm border border-zinc-300 bg-white px-2 py-1 text-[11px] text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
          data-testid={`spec-paragraph-ai-cancel-${paragraphNumber}`}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSubmit(intent.trim())}
          disabled={isPending || !intent.trim()}
          className="inline-flex items-center gap-1 rounded-sm bg-zinc-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
          data-testid={`spec-paragraph-ai-submit-${paragraphNumber}`}
        >
          {isPending && <Loader2 className="h-3 w-3 animate-spin" />}
          Submit
        </button>
      </div>
    </div>
  );
}
